import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeMuteIcon from "@material-ui/icons/VolumeMute";
import { useVoice } from "../../context/Voice/VoiceContext";
import { useSoftphone } from "../../context/Voice/SoftphoneContext";

/**
 * SoftphoneControls — the in-call media control cluster for the browser
 * softphone (Phase 1B POC). v1 ships Mute only (DTMF / speaker picker are
 * deferred per the technical design §13).
 *
 * Gating (locked): renders ONLY when the active call is a softphone call AND
 * the local media leg is actually open — `activeCall.mode === "softphone" &&
 * mediaState === "open"`. In bridge mode (or before the agent leg is live) it
 * renders nothing, so the bar stays byte-for-byte today's bar.
 *
 * Placement: hosted by VoiceCallBar inside the control row, immediately
 * inline-start of the hang-up button, separated by a hairline divider — the
 * destructive action stays the outermost item (Aircall / WhatsApp convention).
 * Logical properties only (RTL-correct under dir="rtl").
 */
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    controls: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(0.75), // 6
      marginInlineStart: theme.spacing(0.5),
      paddingInlineStart: theme.spacing(1),
      // Hairline divider separating media controls from the status cluster.
      borderInlineStart: `1px solid ${
        dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
      }`,
    },
    muteBtn: {
      width: 36,
      height: 36,
      padding: 7,
      borderRadius: "50%",
      transition:
        "background .15s ease, color .15s ease, box-shadow .15s ease",
      // LIVE (not muted) — ghost / neutral.
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: dark ? "rgba(230,247,236,0.75)" : "#5f6f6a",
      "@media (hover:hover)": {
        "&:hover": {
          background: dark ? "rgba(255,255,255,0.11)" : "rgba(15,23,42,0.09)",
        },
      },
    },
    // MUTED — active / danger-tinted so "you are muted" is unmistakable.
    muteBtnMuted: {
      background: dark ? "rgba(255,138,128,0.16)" : "rgba(198,40,40,0.12)",
      color: dark ? "#ff8a80" : "#c62828",
      boxShadow: `inset 0 0 0 1px ${
        dark ? "rgba(255,138,128,0.4)" : "rgba(198,40,40,0.35)"
      }`,
      "@media (hover:hover)": {
        "&:hover": {
          background: dark ? "rgba(255,138,128,0.22)" : "rgba(198,40,40,0.18)",
        },
      },
    },
  };
});

const SoftphoneControls = () => {
  const classes = useStyles();
  const { activeCall } = useVoice();
  const softphone = useSoftphone();
  const { mediaState, isMuted, mute, speakerOn, toggleSpeaker } = softphone || {};

  // Locked gating — softphone leg must be live.
  if (!activeCall || activeCall.mode !== "softphone" || mediaState !== "open") {
    return null;
  }

  const label = isMuted ? "בטל השתקה" : "השתק";
  const spkLabel = speakerOn ? "כבה רמקול" : "רמקול";

  return (
    <div className={classes.controls}>
      <Tooltip title={label}>
        <IconButton
          className={`${classes.muteBtn} ${isMuted ? classes.muteBtnMuted : ""}`}
          onClick={() => mute && mute()}
          aria-label={label}
          aria-pressed={Boolean(isMuted)}
        >
          {isMuted ? (
            <MicOffIcon fontSize="small" />
          ) : (
            <MicIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title={spkLabel}>
        <IconButton
          className={`${classes.muteBtn} ${speakerOn ? classes.muteBtnMuted : ""}`}
          onClick={() => toggleSpeaker && toggleSpeaker()}
          aria-label={spkLabel}
          aria-pressed={Boolean(speakerOn)}
        >
          {speakerOn ? (
            <VolumeUpIcon fontSize="small" />
          ) : (
            <VolumeMuteIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default SoftphoneControls;
